/**
 * Easter clipart — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_seasons`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const easterClipartPack: ToilePack = {
    slug: 'easter-clipart',
    category: 'season',
    name: 'Easter clipart',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 1024, h: 1024},
    readings: [
        {key: 'egg', label: 'Painted egg', subject: 'A single painted egg with stripes and dots, isolated on a plain white ground'},
        {key: 'tulip', label: 'Tulip', subject: 'A single tulip with two leaves, isolated on a plain white ground'},
        {key: 'bunny', label: 'Bunny', subject: 'A sitting bunny seen side on, isolated on a plain white ground'},
    ],
    styles: [
        {key: 'pastel', label: 'Pastel kawaii', style: 'pastel kawaii illustration, rounded shapes, soft outlines, friendly faces', palette: 'Soft pink, mint, lilac, butter yellow and cream', framing: 'Centered on a solid pastel background, built to read clean at small size'},
        {key: 'vintage', label: 'Vintage', style: 'victorian easter card illustration, soft halftone grain, gentle colour', palette: 'Faded rose, sage green, cream and a little gold', framing: 'Centered on aged cream paper with an even margin'},
        {key: 'modern', label: 'Modern minimal', style: 'modern minimal illustration, few flat shapes, no outlines', palette: 'Two or three flat spring colours on white', framing: 'Centered with generous white space around it'},
    ],
    directions: ['a basket instead', 'as a matching pair', 'with a soft drop shadow'],
    directionHint: 'try: a basket instead',
    seo: {
        title: 'Easter Clipart: Painted Egg, Tulip & Bunny',
        metaDescription: 'Nine Easter clipart pieces — painted egg, tulip, bunny — isolated on white in pastel kawaii, vintage, and modern minimal styles.',
        h1: 'Easter clipart: painted egg, tulip, bunny',
        tagline: 'Nine clipart pieces, three motifs, three treatments each.',
        intro: 'Nine Easter clipart images, each isolated clean on white: a striped-and-dotted painted egg, a tulip with two leaves, and a sitting bunny seen side on, in a pastel kawaii, a Victorian vintage, and a modern minimal treatment. Ready to drop straight into a newsletter, flyer, or worksheet.',
        useCases: [
            'a spring newsletter or email header',
            'a classroom worksheet or bulletin board',
            'an invitation or greeting-card layout',
            'a scrapbook page accent',
        ],
        faq: [
            {q: 'Is the background transparent or white?', a: 'Each piece is isolated on a plain white background, ready to place directly or to knock out for a transparent version in any image editor.'},
            {q: 'Can I resize these without losing quality?', a: 'The files are delivered at a size suited to print and web use; for very large prints, scale conservatively to keep edges crisp.'},
            {q: "What's the difference between the three styles?", a: 'Pastel kawaii uses rounded, friendly shapes in soft colour, vintage draws on Victorian Easter-card illustration, and modern minimal keeps to a few flat shapes and generous space.'},
            {q: 'Can I use this clipart in a commercial newsletter or product?', a: 'Yes — the clipart is yours to use in personal and commercial projects, including newsletters, packaging, and printed goods.'},
        ],
    },
}
